import type { AppNotification } from "@a/notifications";
import { cn } from "@u/cn";
import { notificationI18n, notificationRoute, type NotificationType } from "@u/notifications";
import * as Icons from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function NotificationRow({
  n,
  onRead,
  onNavigate,
}: {
  n: AppNotification;
  onRead: (id: string) => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const meta = { ...n.meta, tour_id: n.tour_id ?? n.meta?.tour_id };
  const { titleKey, bodyKey, icon } = notificationI18n(n.type as NotificationType);
  const Icon = (Icons as unknown as Record<string, Icons.LucideIcon>)[icon] ?? Icons.Bell;
  const unread = !n.read_at;

  const handleClick = () => {
    if (unread) onRead(n.id);
    onNavigate?.();
    navigate(notificationRoute(n.type as NotificationType, meta));
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "w-full text-left flex gap-3 px-4 py-3 transition-colors hover:bg-muted/50",
        unread && "bg-primary/5",
      )}
    >
      <span className="mt-0.5 shrink-0 text-muted-foreground">
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground truncate">
          {t(titleKey, meta as Record<string, unknown>)}
        </span>
        <span className="block text-xs text-muted-foreground truncate">
          {t(bodyKey, meta as Record<string, unknown>)}
        </span>
      </span>
      {unread && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />}
    </button>
  );
}
